import { useStore } from '@tanstack/react-form'
import { CheckBox, ColumnMultiSelect, FF, FieldSet, IntegerInput, Select, TextInput, Validators } from 'ui/components'
import { Col, FormRow } from 'ui/themes/globalStyles'
import { useTemplateManageContext } from '~bank-document-templates/manage/context'

export const General = () => {
  const { formApi, data } = useTemplateManageContext()
  const packageValue = useStore(formApi.store, (s) => s.values.package)
  const { typeOptions, levelOptions, frequencyOptions, startDateTypeOptions, packageOptions, stateOptions } = data
  const isOptionalEnabled = packageValue === 'app'

  return (
    <FieldSet legend="General">
      <FormRow>
        <Col>
          <formApi.Field
            name="name"
            validators={{
              onChange: ({ value }) => Validators.required.field(value)
            }}
            children={(field) => (
              <FF required field={field} label="Document name" input={(p) => <TextInput {...p} data-qa="name" />} />
            )}
          />
        </Col>
        <Col>
          <formApi.Field
            name="type"
            validators={{
              onChange: ({ value }) => Validators.required.field(value)
            }}
            children={(field) => (
              <FF
                required
                field={field}
                label="Type"
                input={(p) => <Select {...p} data={typeOptions} data-qa="Type" options-data-qa="Type option" />}
              />
            )}
          />
        </Col>
      </FormRow>
      <FormRow>
        <Col>
          <formApi.Field
            name="level"
            validators={{
              onChange: ({ value }) => Validators.required.field(value)
            }}
            listeners={{
              onChange: ({ value, fieldApi }) => {
                if (value !== 'contact') {
                  fieldApi.form.setFieldValue('contactTypes', null)
                  fieldApi.form.setFieldValue('minimalOwnershipPercent', null)
                }
                if (value !== 'bank_account') {
                  fieldApi.form.setFieldValue('contactTypes', null)
                  fieldApi.form.setFieldValue('minimalOwnershipPercent', null)
                  fieldApi.form.setFieldValue('licenseTypes', null)
                  fieldApi.form.setFieldValue('licenseSubtypes', null)
                  fieldApi.form.setFieldValue('businessTypes', null)
                  fieldApi.form.setFieldValue('entityTypes', null)
                }
                if (value !== 'license') {
                  fieldApi.form.setFieldValue('licenseTypes', null)
                  fieldApi.form.setFieldValue('licenseSubtypes', null)
                }
                if (value !== 'company') {
                  fieldApi.form.setFieldValue('businessTypes', null)
                  fieldApi.form.setFieldValue('entityTypes', null)
                }
              }
            }}
            children={(field) => (
              <FF
                required
                field={field}
                label="Level"
                input={(p) => (
                  <Select
                    {...p}
                    data={levelOptions}
                    data-qa="Level"
                    options-data-qa="Level option"
                    hint="Select the appropriate level"
                  />
                )}
              />
            )}
          />
        </Col>
        <Col>
          <formApi.Field
            name="internal"
            children={(field) => (
              <FF
                field={field}
                label="&nbsp;"
                input={(p) => <CheckBox {...p} data-qa="Internal" checkBoxLabel="Internal" />}
              />
            )}
          />
        </Col>
      </FormRow>
      <FormRow>
        <Col>
          <formApi.Field
            name="frequency"
            validators={{
              onChange: ({ value }) => Validators.required.stringWithPossibleZeroLength(value)
            }}
            children={(field) => (
              <FF
                required
                field={field}
                label="Frequency"
                input={(p) => (
                  <Select
                    {...p}
                    data={frequencyOptions}
                    data-qa="Frequency"
                    options-data-qa="Frequency option"
                    hint="Choose how often the next document period occurs"
                  />
                )}
              />
            )}
          />
        </Col>
        <Col>
          <formApi.Field
            name="startDateType"
            validators={{
              onChange: ({ value }) => Validators.required.stringWithPossibleZeroLength(value)
            }}
            children={(field) => (
              <FF
                field={field}
                required
                label="Start date type"
                input={(p) => (
                  <Select
                    {...p}
                    data={startDateTypeOptions}
                    data-qa="Start date type"
                    options-data-qa="Start date type option"
                    hint="Select the type of start date"
                  />
                )}
              />
            )}
          />
        </Col>
      </FormRow>
      <FormRow>
        <Col>
          <formApi.Field
            name="dueStartDate"
            children={(field) => (
              <FF
                field={field}
                label="&nbsp;"
                input={(p) => <CheckBox {...p} data-qa="Due start date" checkBoxLabel="Due start date" />}
              />
            )}
          />
        </Col>
        <Col>
          <formApi.Field
            name="expirationDelayDays"
            children={(field) => (
              <FF
                field={field}
                label="Expiration delay days"
                input={(p) => <IntegerInput {...p} data-qa="Expiration delay days" />}
              />
            )}
          />
        </Col>
      </FormRow>
      <FormRow>
        <Col>
          <formApi.Field
            name="package"
            listeners={{
              onChange: ({ value, fieldApi }) => {
                if (value !== 'app') {
                  fieldApi.form.setFieldValue('optional', false)
                }
              }
            }}
            children={(field) => (
              <FF
                field={field}
                label="Package"
                input={(p) => (
                  <Select
                    {...p}
                    data={packageOptions}
                    data-qa="Package"
                    options-data-qa="Package option"
                    hint="Select a package type: Application or leave default"
                  />
                )}
              />
            )}
          />
        </Col>
        {isOptionalEnabled && (
          <Col>
            <formApi.Field
              name="optional"
              children={(field) => (
                <FF
                  field={field}
                  label="&nbsp;"
                  input={(p) => <CheckBox {...p} data-qa="Optional" checkBoxLabel="Optional" />}
                />
              )}
            />
          </Col>
        )}
      </FormRow>
      <FormRow>
        <Col>
          <formApi.Field
            name="states"
            children={(field) => (
              <FF
                field={field}
                label="State(s)"
                input={(p) => (
                  <ColumnMultiSelect
                    {...p}
                    data={stateOptions}
                    data-qa="State(s)"
                    placeholder="All"
                    enableSelectAll
                    hint="Leave blank to select all states"
                  />
                )}
              />
            )}
          />
        </Col>
      </FormRow>
    </FieldSet>
  )
}
